import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ExternalLink, RefreshCw } from 'lucide-react';
import { V2PageShell, Card, Button, Pill, EmptyState, Toast } from '../../components/v2';
import { listForms } from '../../services/api';
import useFormsTabs from '../../hooks/useFormsTabs';
import useActiveScope from '../../hooks/useActiveScope';

/**
 * FormsLibraryPage — RC-160 + RC-162d + RC-210.
 *
 * <p>The /forms/library landing — cross-workflow catalogue of form_task
 * nodes. Each row shows workflow name, form_task node label, schema
 * fingerprint (field count + types summary), assignment mode
 * (native_assignee | external_api | approval_center), assignment
 * summary (assignee + deadline + escalation), in-flight assignment
 * count, and a deep-link to open the per-form Designer
 * (/forms/design?wid=&amp;nid=).</p>
 *
 * <p><b>Data source — RC-162d swap.</b> Now consumes the BE catalogue
 * endpoint {@code GET /api/v1/forms} (RC-163a/b). Prior implementation
 * pulled every workflow definition with {@code getWorkflows()} and
 * aggregated form_task nodes client-side — which (a) didn't scale past
 * tenants with many workflows and (b) lacked instance scoping. The BE
 * endpoint performs the JSONB lateral-unnest projection in one
 * round-trip and applies the full two-axis scope contract (tenant +
 * instance, with §2 bubble-up rule) via
 * {@link RbacService#accessibleScopeForRead()} (RC-210 / 210b).</p>
 *
 * <p><b>Chip-narrowed query (RC-210 / 210c).</b> Passes
 * {@code orgId / tenantId / instanceId} from {@code useActiveScope()}
 * to the BE so the Library reflects the active-scope chip pin:
 * - SA pinned to NewTestTenant1 / Dev → only NewTestTenant1's Dev-instance
 *   + tenant-level (instance_id IS NULL) form_tasks.
 * - IA on Dev → enforced to JWT instance + tenant-level cascade.
 * - PA without chip → cross-customer view.</p>
 *
 * <p><b>In-flight column (new in RC-162d).</b> The BE projection
 * returns {@code inFlightAssignmentCount} per form, computed via
 * grouped count over {@code form_task_assignments} JOIN
 * {@code workflow_runs}. Replaces the prior implementation which
 * had no runtime visibility.</p>
 *
 * <p>Built on V2 primitives. Zero inline style.</p>
 */
export default function FormsLibraryPage() {
  // RC-162b — Forms sub-nav (Library | To Do | Designer). The hook
  // derives the active tab from useLocation and wires onChange to
  // navigate(); V2PageShell renders the TabNav natively when the
  // tabs/activeTabId/onTabChange triple is passed in.
  const { tabs, activeTabId, onTabChange } = useFormsTabs();

  // RC-210 — chip narrowing mirrors /api/v1/workflows scope semantics
  // across all three axes (org / tenant / instance). Without these the
  // catalogue would return the caller's full envelope (SA: all tenants
  // in customer; OA: all tenants in org; IA: all instances under their
  // tenant) regardless of the chip — silent cross-tenant / cross-instance
  // leak. The hook returns null for any unset chip dimension; the api
  // client omits null params so non-SA callers continue to get
  // JWT-backfilled scope server-side.
  const { orgId, tenantId, instanceId } = useActiveScope();

  const [entries, setEntries] = useState(null);   // null = not loaded
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await listForms({ orgId, tenantId, instanceId });
      // Defensive unwrap — listForms returns either bare array or {data: array}
      // depending on apiFetch's response envelope (varies per legacy callers).
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setEntries(list);
    } catch (e) {
      setError(e?.message || 'Could not load forms.');
      setEntries([]);
    } finally {
      setRefreshing(false);
    }
  }, [orgId, tenantId, instanceId]);

  // Reload when chip changes (orgId/tenantId/instanceId), or on mount.
  // useCallback above has the chip deps, so [load] picks them up.
  useEffect(() => { load(); }, [load]);

  const actions = (
    <Button variant="secondary" size="sm" iconLeft={<RefreshCw />} onClick={load} loading={refreshing}>
      Refresh
    </Button>
  );

  return (
    <V2PageShell
      title="Forms · Library"
      subtitle="Every form_task across your workflows. Open the designer to edit a form, or view live assignment runtime."
      actions={actions}
      tabs={tabs}
      activeTabId={activeTabId}
      onTabChange={onTabChange}
    >
      {error && (
        <div className="mb-4">
          <Toast tone="danger" title="Could not load library" description={error}
            duration={0} onDismiss={() => setError(null)} />
        </div>
      )}

      {entries == null && (
        <Card><Card.Body>
          <div className="text-[13px] text-v2-ink-secondary py-6 text-center">Loading forms…</div>
        </Card.Body></Card>
      )}

      {entries != null && entries.length === 0 && (
        <EmptyAtChipScope
          orgId={orgId}
          tenantId={tenantId}
          instanceId={instanceId}
          surface="Library"
          onShowUnfiltered={async () => {
            setRefreshing(true);
            setError(null);
            try {
              const res = await listForms({});  // no chip narrowing
              const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
              setEntries(list);
            } catch (e) {
              setError(e?.message || 'Could not load forms.');
            } finally {
              setRefreshing(false);
            }
          }}
        />
      )}

      {entries != null && entries.length > 0 && (
        <Card padding="none">
          <table className="w-full text-[12.5px] text-v2-ink">
            <thead className="bg-v2-muted text-v2-ink-secondary uppercase text-[11px] tracking-wide">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Form</th>
                <th className="text-left px-4 py-2 font-semibold">Workflow</th>
                <th className="text-left px-4 py-2 font-semibold">Mode</th>
                <th className="text-left px-4 py-2 font-semibold">Assignment</th>
                <th className="text-left px-4 py-2 font-semibold">Schema</th>
                <th className="text-left px-4 py-2 font-semibold">In flight</th>
                <th className="text-right px-4 py-2 font-semibold"> </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((row) => (
                <tr key={`${row.workflowId}-${row.nodeId}`}
                    className="border-t border-v2-border-subtle hover:bg-v2-muted">
                  <td className="px-4 py-3">
                    <span className="font-medium text-v2-ink">{row.nodeLabel || row.nodeId}</span>
                    <div className="text-[11px] text-v2-ink-muted mt-0.5 font-mono">{row.nodeId}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-v2-ink">{row.workflowName || '(unnamed workflow)'}</span>
                    {row.workflowVersion != null && (
                      <Pill tone="muted" variant="soft">
                        <span className="ml-1">v{row.workflowVersion}</span>
                      </Pill>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <ModePill mode={row.assignmentMode} />
                  </td>
                  <td className="px-4 py-3 text-v2-ink-secondary">
                    <AssignmentSummary row={row} />
                  </td>
                  <td className="px-4 py-3 text-v2-ink-secondary">
                    <div>{row.fieldCount} field{row.fieldCount === 1 ? '' : 's'}</div>
                    {row.fieldTypes && (
                      <div className="text-[11px] text-v2-ink-muted mt-0.5">{row.fieldTypes}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-v2-ink-secondary">
                    <InFlightPill count={row.inFlightAssignmentCount} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/forms/design?wid=${encodeURIComponent(row.workflowId)}&nid=${encodeURIComponent(row.nodeId)}`}>
                      <Button variant="secondary" size="sm" iconRight={<ExternalLink />}>
                        Designer
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </V2PageShell>
  );
}

function ModePill({ mode }) {
  if (mode === 'native_assignee') return <Pill tone="success" variant="soft">native assignee</Pill>;
  if (mode === 'external_api')    return <Pill tone="cyan"    variant="soft">external API</Pill>;
  return <Pill tone="muted" variant="soft">approval center (legacy)</Pill>;
}

/**
 * RC-FormDesigner-EmptyState (2026-06-23) — diagnostic empty state.
 * Same component shape as FormsDesignerIndexPage's; kept inline rather
 * than extracted to a shared file because the two surfaces have
 * slightly different copy ("Library" vs "Designer") and a shared
 * component would be premature abstraction (only two call sites,
 * surface label differs). Future RC can extract if a 3rd Forms
 * surface adopts the pattern.
 */
function EmptyAtChipScope({ orgId, tenantId, instanceId, surface, onShowUnfiltered }) {
  const hasAnyChip = orgId || tenantId || instanceId;
  return (
    <Card><Card.Body>
      <EmptyState
        icon={<FileText />}
        title={hasAnyChip ? 'No forms at this scope' : 'No forms yet'}
        description={
          hasAnyChip
            ? `Your active scope chip is narrowing the ${surface} view. Forms may exist in other ` +
              `tenants or instances within your role's reach. Try "Show across full scope" below, ` +
              `widen the chip in the top bar, or add a form_task to a workflow at this scope.`
            : `Add a form_task node to a workflow on the canvas to start collecting structured inputs.`
        }
        primaryAction={
          hasAnyChip ? (
            <Button variant="primary" onClick={onShowUnfiltered}>
              Show across full scope
            </Button>
          ) : (
            <Link to="/canvas">
              <Button variant="primary">Go to Workflows</Button>
            </Link>
          )
        }
        secondaryAction={
          hasAnyChip ? (
            <Link to="/canvas">
              <Button variant="secondary">Go to Workflows</Button>
            </Link>
          ) : null
        }
      />
      {hasAnyChip && (
        <div className="mt-3 text-[11.5px] text-v2-ink-muted text-center">
          Active scope:
          {orgId      && <Pill tone="muted" variant="soft"><span className="font-mono">org {orgId.slice(0, 8)}…</span></Pill>}
          {tenantId   && <Pill tone="muted" variant="soft"><span className="font-mono">tenant {tenantId.slice(0, 8)}…</span></Pill>}
          {instanceId && <Pill tone="muted" variant="soft"><span className="font-mono">instance {instanceId.slice(0, 8)}…</span></Pill>}
        </div>
      )}
    </Card.Body></Card>
  );
}

/**
 * Render the BE-rendered {@code assigneeSummary} plus deadline +
 * escalation flag. The BE has already done the heavy lifting (UUID
 * truncation, type detection, etc.) — this just composes the
 * human-readable phrase.
 */
function AssignmentSummary({ row }) {
  const parts = [];
  if (row.assigneeSummary) parts.push(row.assigneeSummary.toLowerCase());
  if (row.deadline)        parts.push(`due ${row.deadline}`);
  if (row.escalationEnabled) parts.push('escalation on');
  if (parts.length === 0) return <span className="text-v2-ink-muted italic">unconfigured</span>;
  return parts.join(' · ');
}

/**
 * In-flight assignment count pill. Zero renders dimmed so the row
 * doesn't draw attention; non-zero is action-toned so a Library scan
 * surfaces forms with live activity.
 */
function InFlightPill({ count }) {
  const n = count == null ? 0 : count;
  if (n === 0) return <span className="text-v2-ink-muted">0</span>;
  return <Pill tone="action" variant="soft">{n}</Pill>;
}
